import { audioAssetById, concepts, instructionAudio, phonemeById, wordById } from '../data/foundations'
import type { Lesson, Word } from '../types/content'
import { resolveLessonContent } from './activityModel'

const conceptById = new Map(concepts.map((concept) => [concept.id, concept]))
const oralWordIds = [...new Set(concepts.filter((concept) => concept.stage === 0).flatMap((concept) => concept.exampleWordIds))]

export function isAudioAssetAvailable(assetId: string): boolean {
  const asset = audioAssetById.get(assetId)
  return Boolean(asset?.path && asset.status !== 'missing' && asset.status !== 'rejected')
}

export function isWordAudioAvailable(word: Word): boolean {
  return isAudioAssetAvailable(word.audioId)
}

/** Keep oral target selection shared between readiness, playback and the activity. */
export function getLessonWord(sourceLesson: Lesson): Word | undefined {
  const lesson = resolveLessonContent(sourceLesson)
  const concept = conceptById.get(lesson.conceptId)
  const explicit = lesson.wordIds.map((id) => wordById.get(id)).find(Boolean)
  if (explicit) return explicit
  const examples = (concept?.exampleWordIds ?? []).map((id) => wordById.get(id))
  if (lesson.mode === 'oral_only') return examples.find(Boolean) ?? oralWordIds.map((id) => wordById.get(id)).find(Boolean)
  return examples.find((word) => word && !word.teacherSupported && !word.isPseudoWord
    && word.decodableAfterConcepts.every((id) => (conceptById.get(id)?.order ?? Infinity) <= (concept?.order ?? -1)))
}

/** All oral choices are spoken, so an unavailable distractor must never be offered. */
export function getOralLessonWords(lesson: Lesson): Word[] {
  const target = getLessonWord(lesson)
  if (!target) return []
  const ids = [...new Set([target.id, ...lesson.wordIds, ...(conceptById.get(lesson.conceptId)?.exampleWordIds ?? []), ...oralWordIds])]
  return ids.map((id) => wordById.get(id)).filter((word): word is Word => Boolean(word && isWordAudioAvailable(word))).slice(0, 3)
}

export function getInstructionForLesson(lesson: Lesson): { id: string; text: string } {
  const oral = lesson.mode === 'oral_only'
  const kind = oral && lesson.conceptId.includes('segment') ? 'segment'
    : oral && lesson.conceptId.includes('blend') ? 'blend' : lesson.type
  const idByType: Record<Lesson['type'], string> = {
    hear: 'instruction_listen_zh', recognition: 'instruction_choose_zh', 'sound-hunt': 'instruction_find_zh',
    blend: 'instruction_blend_zh', segment: 'instruction_segment_zh', decode: 'instruction_read_zh',
    encode: 'instruction_your_turn_zh', 'word-challenge': 'instruction_choose_zh', review: 'instruction_try_again_zh',
  }
  const instruction = instructionAudio.find(([id]) => id === idByType[kind])!
  return { id: instruction[0], text: instruction[1] }
}

const phonemeAudioIds = (ids: string[]) => ids.map((id) => phonemeById.get(id)?.audioId ?? `${id}_ga`)

/** Required files only: text-only answer choices and unused word-bank entries are excluded. */
export function getLessonAudioRequirements(lesson: Lesson): { required: string[]; optional: string[] } {
  const word = getLessonWord(lesson)
  const required = [getInstructionForLesson(lesson).id]
  if (lesson.mode === 'oral_only') {
    if (word) {
      required.push(...phonemeAudioIds(word.phonemeIds))
      if (!lesson.conceptId.includes('same')) required.push(word.audioId)
      if (lesson.conceptId.includes('blend')) required.push(...getOralLessonWords(lesson).map((choice) => choice.audioId))
    } else required.push(`unavailable_word_for_${lesson.id}`)
  } else {
    const soundTask = ['hear', 'recognition', 'sound-hunt'].includes(lesson.type) && lesson.targetPhonemeIds.length > 0
    if (soundTask || !word) required.push(...phonemeAudioIds(lesson.targetPhonemeIds))
    else {
      // Segmented help always uses genuine phoneme files, never letter-name audio.
      required.push(...phonemeAudioIds(word.phonemeIds))
      // Independent reading can be attempted with phoneme help even if the word
      // recording is missing (particularly explicitly labelled silly words).
      if (lesson.type !== 'decode') required.push(word.audioId)
    }
    if (!word && !lesson.targetPhonemeIds.length) required.push(`unavailable_target_for_${lesson.id}`)
  }
  return {
    required: [...new Set(required)],
    optional: ['feedback_success', 'feedback_lesson_complete'],
  }
}

export function getLessonReadiness(lesson: Lesson): { available: boolean; missingAssetIds: string[]; pendingReviewAssetIds: string[] } {
  const { required } = getLessonAudioRequirements(lesson)
  const missingAssetIds = required.filter((id) => !isAudioAssetAvailable(id))
  // Usable in this preview is not equivalent to independently verified for teaching.
  const pendingReviewAssetIds = required.filter((id) => isAudioAssetAvailable(id) && audioAssetById.get(id)?.status !== 'verified')
  return { available: missingAssetIds.length === 0, missingAssetIds, pendingReviewAssetIds }
}
